import { Money } from '@palaccount/money';
import type { ComponentProps, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Amount, Ltr } from '@/components/amount';
import type { TwoWayState } from '@/features/sales/totals-mirror';
import { cn } from '@/lib/utils';

/**
 * Wave 4C — ONE totals footer for every priced document: invoice, quotation,
 * sales order, bill, purchase order, both credit-note editors and the direct
 * purchase return.
 *
 *   Subtotal                          F-5  Σ line nets
 *   Discount            [ % ][ amt ]  F-2  two views of one number
 *   After discount (before tax)       F-3  subtotal − discount: the taxable base
 *   VAT                 [ % ][ amt ]  F-4  charged on F-3
 *   Total                  CUR  9.99  F-7  the largest figure, its currency CODE
 *                                         beside it (GEN-108): a total in EUR
 *                                         must not read as the company's money
 *
 * The screens drew the first, second, fourth and fifth rows by hand, each a
 * little differently, and only the stand-alone return drew the third. The
 * controls stay the screen's (a document whose VAT is a single % box passes
 * that box); the rows, their order and the subtraction are drawn here, once.
 *
 * NOT HERE, ON PURPOSE: tax-inclusive pricing (a price that already contains
 * VAT, backed out to a net). It changes what F-3 and F-4 MEAN, and that needs
 * an accounting ruling before any screen draws it — see the gap analysis's
 * wave 5 "tax-inclusive mode". Until then every figure here is tax-exclusive.
 */
export function DocumentTotals({
  currency,
  subtotal,
  discountAmount,
  discountControl,
  taxAmount,
  vatControl,
  grandTotal,
  totalLabel,
}: {
  currency: string;
  subtotal: string;
  /** The discount in money; null while the typed discount is not a figure. */
  discountAmount: string | null;
  /** The screen's discount input(s); without one the amount is shown. */
  discountControl?: ReactNode;
  taxAmount: string | null;
  vatControl?: ReactNode;
  grandTotal: string | null;
  /** "Credit total", "Debit total"…; defaults to the plain "Total". */
  totalLabel?: string;
}) {
  const { t } = useTranslation();
  const afterDiscount = afterDiscountOf(subtotal, discountAmount, currency);
  return (
    <>
      <div className="flex items-center justify-between text-sm">
        <span>{t('sales.subtotal')}</span>
        <Amount value={subtotal} currency={currency} />
      </div>
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="shrink-0">{t('sales.discount')}</span>
        {discountControl ?? (discountAmount !== null ? <Amount value={discountAmount} currency={currency} /> : '—')}
      </div>
      <div className="flex items-center justify-between text-sm" data-testid="totals-after-discount">
        <span>{t('sales.afterDiscount')}</span>
        {afterDiscount !== null ? <Amount value={afterDiscount} currency={currency} /> : '—'}
      </div>
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="shrink-0">{t('sales.vat')}</span>
        {vatControl ?? (taxAmount !== null ? <Amount value={taxAmount} currency={currency} /> : '—')}
      </div>
      <div className="flex items-center justify-between rounded-lg bg-primary px-4 py-3 text-primary-foreground">
        <span className="text-sm font-medium">{totalLabel ?? t('sales.grandTotal')}</span>
        <span className="flex items-baseline gap-2">
          <Ltr value={currency} className="text-xs font-medium opacity-80" />
          <span className="text-lg font-bold">
            {grandTotal !== null ? <Amount value={grandTotal} currency={currency} /> : '—'}
          </span>
        </span>
      </div>
    </>
  );
}

/** F-3 through Money, never JS arithmetic; '—' while either side is not a figure. */
export function afterDiscountOf(subtotal: string, discountAmount: string | null, currency: string): string | null {
  if (discountAmount === null) return null;
  try {
    return Money.of(subtotal, currency).minus(Money.of(discountAmount, currency)).toFixed2();
  } catch {
    return null;
  }
}

/**
 * The %/amount pair the invoice family draws for its discount and its VAT:
 * typing either side makes it the basis, the other side shows the computed
 * figure. Labels per kind, so a screen reader hears which box is which.
 */
export function TwoWayPair({
  kind,
  state,
  onChange,
  pct,
  amount,
  disabled,
}: {
  kind: 'discount' | 'vat';
  state: TwoWayState | undefined;
  onChange: (state: TwoWayState) => void;
  /** The % shown while the basis is the amount (or nothing is typed). */
  pct: string;
  /** The amount shown while the basis is the % (or nothing is typed). */
  amount: string;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1">
      <div className="relative">
        <PairInput
          aria-label={kind === 'discount' ? t('sales.discountPctLabel') : t('sales.vatPctLabel')}
          className="w-[72px] pe-5"
          value={state?.basis === 'pct' ? state.value : pct}
          onChange={(event) => onChange({ basis: 'pct', value: event.target.value })}
          disabled={disabled}
        />
        <span className="pointer-events-none absolute end-1.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          %
        </span>
      </div>
      <PairInput
        aria-label={kind === 'discount' ? t('sales.discountAmountLabel') : t('sales.vatAmountLabel')}
        className="w-[106px]"
        value={state?.basis === 'amount' ? state.value : amount}
        onChange={(event) => onChange({ basis: 'amount', value: event.target.value })}
        disabled={disabled}
      />
    </div>
  );
}

/** The editors' CellInput, as the footer pair draws it: bordered, figures in mono. */
function PairInput({ className, ...props }: ComponentProps<'input'>) {
  return (
    <input
      dir="ltr"
      inputMode="decimal"
      className={cn(
        'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-2 text-sm outline-none transition-colors',
        'hover:border-input focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-70',
        'text-end font-mono tabular-nums',
        className,
      )}
      {...props}
    />
  );
}
